import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/site";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import ProductCard from "../components/ProductCard";
import ProductEnquiryDialog from "../components/ProductEnquiryDialog";
import CheckoutDialog from "../components/CheckoutDialog";
import DispensingEquipment from "../components/DispensingEquipment";
import { Skeleton } from "../components/ui/skeleton";

const TABS = [{ id: "all", label: "All" }, ...CATEGORIES];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  const shown = useMemo(() => {
    const list = items ?? [];
    return list.filter(
      (p) => (cat === "all" || p.category === cat) && `${p.name} ${p.pack_size} ${p.description}`.toLowerCase().includes(q.toLowerCase())
    );
  }, [items, cat, q]);

  return (
    <>
      <Seo title="Products — Tata DEF, Gulf AdBlue, Buckets, IBC Tanks & Lubricating Oils" description="Buy Tata DEF and Gulf AdBlue 20 L buckets and enquire for IBC tanks and lubricating oils from MHM SUPER ADBLUE, West Bengal." />
      <PageHeader testid="products-header" eyebrow="Product catalogue" lines={["AdBlue &", { text: "Lubricants", cls: "text-brand" }]} intro="Tata, Gulf, Eicher and BharatBenz AdBlue, AdBlue buckets, IBC tanks and lubricating oils. Buy online or enquire for bulk rates." image="/img/real/tata-def-poster.jpg" />
      <section className="bg-[var(--paper)] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="-mx-5 overflow-x-auto px-5 lg:mx-0 lg:px-0" data-lenis-prevent>
              <div className="flex w-max gap-2 rounded-full border border-navy/10 bg-white p-1.5">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`product-category-${t.id}`}
                    onClick={() => setParams(t.id === "all" ? {} : { cat: t.id })}
                    className={`relative whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${cat === t.id ? "text-white" : "text-navy hover:text-brand"}`}
                  >
                    {cat === t.id && <motion.span layoutId="cat-pill" className="absolute inset-0 rounded-full bg-navy" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                    <span className="relative">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <label className="relative block lg:w-80">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="product-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="field !rounded-full !pl-11" />
            </label>
          </div>

          {cat === "all" && !q && items !== null ? (
            CATEGORIES.map((c) => {
              const group = shown.filter((p) => p.category === c.id);
              if (!group.length) return null;
              return (
                <div key={c.id} data-testid={`product-section-${c.id}`} className="mt-16 first:mt-12">
                  <div className="flex items-end justify-between border-b border-navy/10 pb-4">
                    <h2 className="font-display text-4xl md:text-5xl font-extrabold uppercase text-navy">{c.label}</h2>
                    <span className="font-mono text-sm text-slate-500">{group.length} {group.length === 1 ? "product" : "products"}</span>
                  </div>
                  <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {group.map((p, i) => <ProductCard key={p.id} p={p} index={i} onEnquire={setActive} onBuy={setBuying} />)}
                  </div>
                </div>
              );
            })
          ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items === null &&
              [0, 1, 2].map((i) => <Skeleton key={i} className="h-[520px] rounded-[22px]" />)}
            {shown.map((p, i) => (
              <ProductCard key={p.id} p={p} index={i} onEnquire={setActive} onBuy={setBuying} />
            ))}
          </div>
          )}
          {items !== null && shown.length === 0 && (
            <p data-testid="products-empty" className="mt-12 rounded-2xl border border-dashed border-navy/20 p-12 text-center text-slate-600">
              No products in this category yet. Contact us on WhatsApp for availability.
            </p>
          )}
        </div>
      </section>
      <DispensingEquipment />
      <ProductEnquiryDialog product={active} onClose={() => setActive(null)} />
      <CheckoutDialog product={buying} onClose={() => setBuying(null)} />
    </>
  );
}
